/**
 * @format
 * @module utils/story
 * @description Shared methods for article stories
 */
import tgamConfig from "config/tgam";
import { getProp } from "utils/utils";

/**
 * @method getStoryColor
 * @description Returns the paywall colour of an article based on page meta data
 * @param {String} color
 * @param {Object} config
 * @returns {Boolean}
 */
export function getStoryColor(color, config = tgamConfig) {
  return getProp(config, "meta.paywallStatus", "").toLowerCase() === color;
}

/**
 * @method isRedArticle
 * @description whether the current article is red
 * @returns {Boolean}
 */
export function isRedArticle() {
  return getStoryColor("red");
}

/**
 * @method isYellowArticle
 * @description whether the current article is yellow
 * @returns {Boolean}
 */
export function isYellowArticle() {
  return getStoryColor("yellow");
}

export function isYellowCoronaArticle(config = tgamConfig) {
  return getProp(config, "meta.seoKeywords", "").toLowerCase().includes("coronayellow");
}

/**
 * Determine whether a given article is "franchised content", based on its URL
 * Important: This function must remain up-to-date with its JSP equivalent: "tags/arc/isFranchisedContent.tag"
 * @param {String} storyURL - The article's canonical URL
 * @returns {Boolean}
 */
export function isFranchisedContent(storyURL) {
  return [
    /\/arts\/books\-and\-media\/bestsellers\//im,
    /\/arts\/books\/bestsellers\//im,
    /\/business\/briefing\//im,
    /\/business\/careers\/career\-advice\//im,
    /\/business\/careers\/management\//im,
    /\/business\/careers\/management\/board\-games\//im,
    /\/business\/careers\/management\/executive\-compensation\//im,
    /\/business\/economy\/economic\-insight\//im,
    /\/business\/rob\-magazine\/top\-1000\//im,
    /\/business\/rob\-magazine\/top\-1000\/rankings\//im,
    /\/business\/streetwise\//im,
    /\/cannabis\/business\//im,
    /\/cannabis\/investing\//im,
    /\/globe\-investor\/inside\-the\-market\//im,
    /\/globe\-investor\/investment\-ideas\/number\-cruncher\//im,
    /\/globe\-investor\/investment\-ideas\/strategy\-lab\//im,
    /\/investing\/investment\-ideas\/number\-cruncher\//im,
    /\/investing\/investment\-ideas\/strategy\-lab\//im,
    /\/investing\/markets\/inside\-the\-market\//im,
    /\/report\-on\-business\/careers\/career\-advice\//im,
    /\/report\-on\-business\/careers\/career\-advice\/life\-at\-work\//im,
    /\/report\-on\-business\/careers\/management\//im,
    /\/report\-on\-business\/careers\/management\/board\-games\//im,
    /\/report\-on\-business\/careers\/management\/executive\-compensation\//im,
    /\/report\-on\-business\/economy\/economic\-insight\//im,
    /\/report\-on\-business\/rob-magazine\/top\-1000\//im,
    /\/report\-on\-business\/rob-magazine\/top\-1000\/rankings\//im,
    /\/report\-on\-business\/streetwise\//im,
    /\/report\-on\-business\/top\-business\-stories\//im
  ].some(urlPattern => urlPattern.test(storyURL) === true);
}

/**
 * @method getPublishedDate
 * @description First datetime an article was 'published', or first displayed on site.
 * Duplicates the logic from file: tags/arc/storyPublishedDate.tag
 * @param {Object} body - of story ANS
 * @returns {Date}
 */
export function getPublishedDate(body) {
  return new Date(getPublishedDateStr(body));
}

/**
 * @method getPublishedDateStr
 * @description First datetime an article was 'published', or first displayed on site.
 * Manually set by editorial. Not updated upon article re-publish.
 * @param {Object} body - of story ANS
 * @returns {String}
 */
export function getPublishedDateStr(body) {
  return body.display_date;
}

/**
 * @method getUpdatedDate
 * @description Last datetime an article was (re)published.
 * Duplicates the logic from file: tags/arc/storyUpdatedDate.tag
 * @param {Object} body - of story ANS
 * @returns {Date}
 */
export function getUpdatedDate(body) {
  return new Date(getUpdatedDateStr(body));
}

/**
 * @method getUpdatedDateStr
 * @description Last datetime an article was (re)published.
 * Automatically updated upon article re-publish.
 * @param {Object} body - of story ANS
 * @returns {String}
 */
export function getUpdatedDateStr(body) {
  return body.publish_date;
}

/**
 * @method hasLeadFigure
 * @description Whether or not the first element with articleBodySelector contains a <figure> tag.
 * @param {String} articleBodySelector
 * @returns {Boolean}
 */
export function hasLeadFigure(articleBodySelector = ".js-c-article-body") {
  const articleBody = document.querySelector(articleBodySelector);
  if (!articleBody) {
    return false;
  }
  // The first "figure" condition "figure" handles the case where the tag of the first content element is "FIGURE"
  // The second "figure" condition accounts for nested items within the first content element.
  // (e.g., <div><div><figure>...</figure></div></div>)
  const firstContentElement = articleBody.children[0];
  return (
    firstContentElement &&
    (firstContentElement.tagName === "FIGURE" ||
      firstContentElement.getElementsByTagName("figure").length !== 0)
  );
}
